import React, { useCallback, useEffect, useRef, useState } from 'react';
import MessageThread from './MessageThread';
import { useLanguage } from '../../components/LanguageContext';
import { PmMessage, MessageChannel, ChatTopic, ownerChannel, tenantChannel, pollWhileActive } from './messaging';
import { uiError } from '../../i18n/locale';

// ============================================================
// CustomerMessages — the customer's direct line to the management team.
//
// ONE component for both kinds of customer. An owner asking "did the AC
// engineer come?" and a tenant asking "when is someone fixing the AC?" are
// the same screen, the same thread model and the same team answering; only
// the channel differs (see messaging.ts), and that is a prop. Two copies of
// this screen would drift apart within a month.
//
// One conversation, not a ticket system. Nobody should have to pick a
// property, a category and a priority before they can ask a question — the
// manager answering already knows whose portfolio or flat it is. The one
// choice an owner makes is real estate vs property management, because that
// decides WHO answers (their Executive Assistant or the property team).
// Anything that needs to become a job is still raised as maintenance.
//
// Distinct from the AI assistant (ChatWidget): that one answers instantly and
// escalates to WhatsApp. This is the human team, in writing, kept in the app.
// ============================================================

const POLL_MS = 12000;

interface Props {
  /** ownerChannel or tenantChannel. */
  channel?: MessageChannel;
  customerName?: string;
  /** Showcase / film mode: sample conversation, no network. */
  demoMode?: boolean;
  onBack?: () => void;
  /** Lets a badge elsewhere clear once the thread has been read. */
  onUnreadChange?: (n: number) => void;
}

const demoThread = (name: string, de: boolean): PmMessage[] => {
  const t = (mins: number) => new Date(Date.now() - mins * 60000).toISOString();
  return de ? [
    { id: 'd1', threadId: 'demo', from: 'admin', authorName: 'Marilyn · Verwaltung', createdAt: t(2880), body: 'Guten Morgen, die Wohnungsübergabe in Sobha Hartland ist für Donnerstag 11:00 Uhr bestätigt. Wir senden Ihnen danach das Protokoll.' },
    { id: 'd2', threadId: 'demo', from: 'owner', authorName: name, createdAt: t(2820), body: 'Perfekt, vielen Dank. Ist die Klimaanlage vorher noch gewartet worden?' },
    { id: 'd3', threadId: 'demo', from: 'admin', authorName: 'Marilyn · Verwaltung', createdAt: t(120), body: 'Ja — die Wartung war gestern, der Bericht liegt unter Dokumente. Kosten AED 420, unter Ihrem Freigabelimit.' },
  ] : [
    { id: 'd1', threadId: 'demo', from: 'admin', authorName: 'Marilyn · Management', createdAt: t(2880), body: 'Good morning — the handover at Sobha Hartland is confirmed for Thursday 11:00. We will send the signed snagging report straight after.' },
    { id: 'd2', threadId: 'demo', from: 'owner', authorName: name, createdAt: t(2820), body: 'Perfect, thank you. Was the AC serviced before that?' },
    { id: 'd3', threadId: 'demo', from: 'admin', authorName: 'Marilyn · Management', createdAt: t(120), body: 'Yes — serviced yesterday, the report is under Documents. AED 420, inside your approval limit.' },
  ];
};

const CustomerMessages: React.FC<Props> = ({
  channel = ownerChannel, customerName, demoMode, onBack, onUnreadChange,
}) => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const de = language === 'de';
  const [messages, setMessages] = useState<PmMessage[]>(() => (demoMode ? demoThread(customerName || 'You', de) : []));
  const [loading, setLoading] = useState(!demoMode);
  // Owners say what the conversation is about, which routes it: their
  // Executive Assistant for real estate, the property team for management.
  // A tenant's questions are always property management, so they never ask.
  const asksTopic = channel !== tenantChannel;
  const [topic, setTopic] = useState<ChatTopic | null>(demoMode ? 'pm' : null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A poll must never clobber a message halfway through sending, and must
  // never re-show one already replaced by the server's copy.
  const inFlight = useRef(false);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (demoMode || inFlight.current) return;
    inFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const { thread, messages: msgs } = await channel.fetchThread();
      setMessages(msgs || []);
      if (thread && thread.topic) setTopic((cur) => cur || thread.topic || null);
      setError(null);
      if (thread && thread.unreadForParticipant > 0) {
        // Opening the thread IS reading it.
        channel.markRead().then(() => onUnreadChange?.(0)).catch(() => { /* badge only */ });
      } else {
        onUnreadChange?.(0);
      }
    } catch (e: any) {
      // A first load that fails leaves the screen empty, so it has to say
      // why. A failed POLL does not: what is on screen is still correct, and
      // an error bar over it would be noise.
      if (!opts.silent) setError(uiError(e?.message, t('omLoadFailed')));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [channel, demoMode, onUnreadChange, t]);

  useEffect(() => { load(); }, [load]);

  // Only while this screen is open, the app is in front, and someone is
  // actually there — see pollWhileActive. Ten minutes of grace, because
  // reading a thread and waiting for an answer is not the same as being gone.
  useEffect(() => {
    if (demoMode) return;
    return pollWhileActive(() => load({ silent: true }), POLL_MS, 10 * 60 * 1000);
  }, [demoMode, load]);

  const send = useCallback(async (body: string) => {
    const optimistic: PmMessage = {
      id: `tmp-${Date.now()}`,
      threadId: 'pending',
      body,
      from: 'owner',
      authorName: customerName || t('omYou'),
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    if (demoMode) return;
    setSending(true);
    try {
      const { message } = await channel.send(body, asksTopic ? topic : null);
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? message : m)));
      setError(null);
    } catch (e: any) {
      // Take the bubble back rather than leave one that looks delivered and
      // never was.
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setError(uiError(e?.message, t('omSendFailed')));
    } finally {
      setSending(false);
    }
  }, [channel, demoMode, customerName, t, asksTopic, topic]);

  // Choosing re-routes at once (not only with the next message), so a change
  // of mind moves an unanswered question to the right person straight away.
  const chooseTopic = useCallback((next: ChatTopic) => {
    if (next === topic) return;
    setTopic(next);
    if (demoMode || !messages.length) return;
    channel.setTopic(next).catch(() => { /* the next message carries it too */ });
  }, [channel, demoMode, messages.length, topic]);

  const topicOptions: { id: ChatTopic; label: string }[] = [
    { id: 'realestate', label: de ? 'Meine Immobilie / Investment' : 'My property / real estate' },
    { id: 'pm', label: de ? 'Hausverwaltung' : 'Property management' },
  ];
  const topicBanner = asksTopic ? (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid #EFE7D6', background: '#FFFDF8' }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: '#6b6b6b', marginBottom: 8 }}>
        {topic
          ? (de ? 'Ihre Nachricht geht an:' : 'Your message goes to:')
          : (de ? 'Worum geht es? Bitte wählen Sie, damit die richtige Person antwortet.' : 'What is this about? Choose so the right person answers.')}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {topicOptions.map((o) => {
          const on = topic === o.id;
          return (
            <button key={o.id} type="button" onClick={() => chooseTopic(o.id)} aria-pressed={on}
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                border: `1.5px solid ${on ? '#000' : '#E3D7BC'}`, background: on ? '#000' : '#fff', color: on ? '#fff' : '#1B1B1B',
              }}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  ) : undefined;

  return (
    <MessageThread
      title={t('omTitle')}
      subtitle={t('omSubtitle')}
      meSide="participant"
      messages={messages}
      loading={loading}
      sending={sending}
      error={error}
      banner={topicBanner}
      disabled={asksTopic && !topic}
      emptyTitle={t('omEmptyTitle')}
      emptyText={t('omEmptyText')}
      placeholder={t('omPlaceholder')}
      sendLabel={t('omSend')}
      onSend={send}
      onBack={onBack}
      locale={locale}
    />
  );
};

export default CustomerMessages;
